import { placeholder } from "@codemirror/view";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { FormControl, TagsInput, TextArea, TextInput } from "../../components/form";
import { Row, Section } from "../../components/layout";
import { CodeEditor } from "../../components/code-editor";
import { Button, PrimaryButton } from "../../components/button";
import { Icon } from "../../components/icon";
import el from "@cypherpotato/el";
import { Link } from "../../components/link";
import { runOperation } from "../../ui/operation";
import { find, valueFrom } from "../../util";
import { api } from "../../api";
import page from "page";
import { CommandBar } from "../../components/command-bar";
import { requestAiEdit } from "../../ui/ai-edit";
import { acceptDiff } from "../../ui/accept-diff";
import { closeFlyout, showFlyout } from "../../ui/flyout";
import { Heading, MutedText, Paragraph } from "../../components/text";
import { Badge, BadgeList } from "../../components/badge";
import { ToolNamePicker } from "../../components/tool-name-picker";
import { TabControl } from "../../components/tabcontrol";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { displayFormControl } from "../../ui/alert";
import { JsonControl } from "../../components/json-control";

export default function (ctx) {

    const skillId = ctx.params.id;
    const isCreating = skillId === 'create';

    const skill = el.state.create(null);

    if (!isCreating) {
        runOperation("Loading skill...", async () => {
            const response = await api.getJson('/api/v1/skills/' + skillId);
            skill.value = response.data;
        });
    }

    function handleSave() {
        const skill = {
            slug: valueFrom("skill-slug"),
            description: valueFrom("skill-description"),
            instructions: valueFrom("skill-instructions"),
            options: {
                allowedToolsNames: valueFrom("skillVisibleTools"),
                instructionSources: valueFrom("skillInstructionSources")
            }
        }

        runOperation(isCreating ? "Creating skill..." : "Saving skill...", async () => {
            if (isCreating) {
                const result = await api.postJson('/api/v1/skills', skill);
                page('/dashboard/skills/' + result.data.id);
            } else {
                await api.putJson('/api/v1/skills/' + skillId, skill);
            }
        });
    }

    return Dashboard(
        Section(
            Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                onClick() {
                    history.go(-1);
                    page();
                }
            })
        ),
        DashboardTitle(
            isCreating ? "Create new skill" : "Edit skill",
            isCreating ? "Fill in the details to create a new skill." : "Edit the details of your skill below."
        ),
        Section(
            CommandBar(
                PrimaryButton(Icon("save-line"), "Save skill", {
                    onClick() {
                        handleSave();
                    }
                }),
                Button(
                    Icon("quill-pen-ai-line"),
                    "Edit with AI",
                    {
                        onClick() {
                            requestAiEdit({
                                inputData: {
                                    instructions: valueFrom("skill-instructions"),
                                    description: valueFrom("skill-description")
                                },
                                subject: "skill",
                                description: "Edit the instructions for this skill",
                                responseSchema: {
                                    type: "object",
                                    properties: {
                                        instructions: { type: "string", description: "Detailed instructions for this skill" },
                                        description: { type: "string", description: "Brief description of what this Skill does and when to use it" }
                                    }
                                }
                            }).then((result) => {
                                acceptDiff(valueFrom("skill-instructions"), result.instructions).then(_ => {
                                    find("skill-instructions").value = result.instructions;
                                    find("skill-description").value = result.description;
                                })
                            })
                        }
                    }
                )
            )
        ),
        el.state.listening([skill], () =>
            Section(
                TabControl([
                    {
                        title: "Skill editor",
                        contents: el.fragment(
                            FormControl("Slug",
                                TextInput("skill-slug", "your-skill-name, only alphanumeric and hyphens", { value: skill.value?.slug }),
                                { style: "max-width: 400px;" }
                            ),
                            FormControl("Description",
                                TextArea("skill-description", skill.value?.description, {
                                    placeholder: "Brief description of what this Skill does and when to use it",
                                    rows: 3
                                })
                            ),
                            FormControl("Instructions",
                                CodeEditor({
                                    value: skill.value?.instructions || "",
                                    language: "markdown",
                                    height: "500px",
                                    props: {
                                        id: "skill-instructions"
                                    }
                                })
                            )
                        )
                    },
                    {
                        title: "Options",
                        contents: el.fragment(
                            Heading(
                                "Skill Settings",
                                "Define the basic configuration for your Skill, including available tools and remote instructions."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "skillInstructionSources",
                                        value: skill.value?.options.instructionSources ?? [],
                                        renderValue(value) {
                                            return Button(value?.length + " source(s)");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value, resize: false }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "The instruction source list must be an array of objects."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Remote instruction sources"),
                                        Paragraph("Specify remote sources to obtain the instructions that will be used in the skill instruction."),
                                    )
                                ),
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "skillVisibleTools",
                                        value: skill.value?.options.allowedToolsNames,
                                        renderValue(value) {
                                            return value?.length && BadgeList(value.slice(0, 3).map(r => Badge(r))) || MutedText("None selected");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ToolNamePicker(null, { value }),
                                                onConfirm: callback,
                                                dialogSize: "medium",
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Skill visible tools"),
                                        Paragraph("Specify one or more tools that will always be visible during the scope of the skill.")
                                    )
                                )
                            )
                        )
                    }
                ])
            )
        )
    )
}